/**
 * Shared shapes for the import dashboard (spec 2026-07-27-10 §3/§7).
 *
 * The summary endpoint returns a free-form jsonb blob for completed imports
 * (it serves `imports.stats` verbatim), so the generated OpenAPI types are a
 * loose `Record<string, unknown>` there. These narrow interfaces are the single
 * place where that blob is given a shape — components never index into `any`.
 */

export type ImportStatus = 'running' | 'completed' | 'failed' | 'cancelled'

export interface ImportRow {
    uid: string
    label: string
    status: ImportStatus
    started_at: string
    finished_at?: string | null
    stats?: Record<string, unknown> | null
}

export interface ImportFunnel {
    deposited: number
    unique: number
    classified: number
    exploited: number
    extracted_values: number
    // Archive entries that never became a document (spec 2026-08-16-07 §1).
    // Absent from summaries frozen before that shipped, hence the defaults in
    // EMPTY_FUNNEL below.
    skipped_duplicates: number
    skipped_junk: number
    // Archive entries that could not be stored (spec 2026-10-04-02 §2).
    failed_entries: number
}

/**
 * One folder of the deposit tree. `document_count` is recursive and always
 * exact — `truncated` says the node's children were cut by the depth (12) or
 * children (200) cap, never that the count is approximate.
 *
 * The synthetic "unfiled" node has an empty `name` AND an empty `path`: those
 * documents carry no folder, so there is no prefix to drill into.
 */
export interface ImportFolderNode {
    name: string
    path: string
    document_count: number
    children?: ImportFolderNode[]
    truncated?: boolean
}

export interface ImportReview {
    unclassified: number
    extraction_gaps: number
}

/**
 * What the deposit cost in paid AI calls (spec 2026-08-16-08 §3). Additive:
 * summaries frozen before it shipped simply lack the key, and the dashboard
 * hides the stat rather than claiming the deposit was free.
 */
export interface ImportAIUsage {
    cost_usd: number
    input_tokens: number
    output_tokens: number
}

/**
 * The deposit-to-structured-portfolio time (spec 2026-10-27-11). Additive:
 * null for an import older than the feature ("not measured"), never a zero.
 */
export interface ImportTimingByJobType {
    job_type: string
    jobs: number
    executions: number
    run_ms: number
    p95_run_ms: number
    queue_ms: number
}

export interface ImportTiming {
    live: boolean
    machine_ms: number
    read_ms: number
    extraction_ms: number
    go_wait_ms: number
    elapsed_ms: number
    waiting_for_go_since: string | null
    figures: { read_extract_ms: number; generate_ms: number; agents_ms: number; total_ms: number }
    tiers: { tier: number; released_at: string; settled_at: string | null }[]
    documents: { count: number; settled: number; pages: number; p50_machine_ms: number; p95_machine_ms: number }
    by_job_type: ImportTimingByJobType[]
    after_extraction: { documents: number; last_end: string | null }
}

export interface ImportEntityCount {
    type: string
    asset_type?: string
    count: number
    from_documents: number
}

export interface ImportDocumentTypeCount {
    code: string
    count: number
}

export interface ImportGap {
    code: string
    severity: 'warning' | 'info'
    count: number
    label?: string
    filter?: Record<string, unknown>
}

export interface ImportProvenanceRow {
    entity_type: string
    entity_uid: string
    entity_name?: string
    property_code?: string
    value?: unknown
    document_uid?: string | null
    document_name?: string
    page?: number | null
    created_at: string
}

export interface ImportSummary {
    import: {
        uid: string
        label: string
        status: ImportStatus
        started_at: string
        finished_at?: string | null
        extraction_started_at?: string | null
        extraction_tier?: number | null
        awaiting_extraction?: number
    }
    funnel: ImportFunnel
    entities: ImportEntityCount[]
    document_types: ImportDocumentTypeCount[]
    gaps: ImportGap[]
    recent_values: ImportProvenanceRow[]
    // Both are additive (spec 2026-08-16-07 §1): a summary frozen by an older
    // backend simply lacks them and the page hides the sections.
    folder_tree: ImportFolderNode[]
    // The root list is capped at 200 folders. Unlike a `truncated` node — whose
    // count still covers its cut subtree — the dropped root folders are absent
    // from `folder_tree` entirely, so the tree must say so out loud.
    folder_tree_truncated: boolean
    folder_tree_omitted_folders: number
    folder_tree_omitted_documents: number
    review: ImportReview | null
    // null — not a zeroed object — when the frozen blob predates the rollup.
    ai_usage: ImportAIUsage | null
    // null when nothing waits on an approval (spec 2026-10-12-11).
    pending_approval: ImportPendingApproval | null
    // null when the import was not measured (spec 2026-10-27-11).
    timing: ImportTiming | null
    computed_at?: string
}

export interface ImportPendingApproval {
    assets: number
    documents: number
    // The to_approve assets holding the paused documents (at most 10).
    blocking?: { uid: string; name: string; documents: number }[]
}

const EMPTY_FUNNEL: ImportFunnel = {
    deposited: 0,
    unique: 0,
    classified: 0,
    exploited: 0,
    extracted_values: 0,
    skipped_duplicates: 0,
    skipped_junk: 0,
    failed_entries: 0,
}

/**
 * Normalizes the summary payload so every consumer can assume the arrays and
 * the funnel exist. A completed import serves a frozen blob written by an older
 * backend, so a missing key is a real possibility rather than a paranoia.
 */
export function normalizeSummary(raw: unknown): ImportSummary | null {
    if (!raw || typeof raw !== 'object') return null
    const r = raw as Record<string, any>
    if (!r.import) return null

    return {
        import: r.import,
        funnel: { ...EMPTY_FUNNEL, ...(r.funnel ?? {}) },
        entities: Array.isArray(r.entities) ? r.entities : [],
        document_types: Array.isArray(r.document_types) ? r.document_types : [],
        gaps: Array.isArray(r.gaps) ? r.gaps : [],
        recent_values: Array.isArray(r.recent_values) ? r.recent_values : [],
        folder_tree: Array.isArray(r.folder_tree) ? r.folder_tree : [],
        folder_tree_truncated: r.folder_tree_truncated === true,
        folder_tree_omitted_folders: Number(r.folder_tree_omitted_folders) || 0,
        folder_tree_omitted_documents: Number(r.folder_tree_omitted_documents) || 0,
        // `null` — not a zeroed object — so the card can tell "an old blob has
        // no review section" from "a fresh deposit with nothing to review".
        review: r.review && typeof r.review === 'object'
            ? {
                unclassified: Number(r.review.unclassified) || 0,
                extraction_gaps: Number(r.review.extraction_gaps) || 0,
            }
            : null,
        // Same null-vs-zero distinction as `review`: "this blob has no AI
        // figures" is not "this deposit cost nothing".
        ai_usage: r.ai_usage && typeof r.ai_usage === 'object'
            ? {
                cost_usd: Number(r.ai_usage.cost_usd) || 0,
                input_tokens: Number(r.ai_usage.input_tokens) || 0,
                output_tokens: Number(r.ai_usage.output_tokens) || 0,
            }
            : null,
        pending_approval:
            r.pending_approval && typeof r.pending_approval === 'object'
                ? {
                    assets: Number(r.pending_approval.assets) || 0,
                    documents: Number(r.pending_approval.documents) || 0,
                    blocking: Array.isArray(r.pending_approval.blocking)
                        ? r.pending_approval.blocking.map((b: Record<string, unknown>) => ({
                            uid: String(b.uid),
                            name: String(b.name ?? ''),
                            documents: Number(b.documents) || 0,
                        }))
                        : [],
                }
                : null,
        timing: normalizeTiming(r.timing),
        computed_at: r.computed_at,
    }
}

/**
 * Renders an extracted value for the provenance feed. Objects are JSON-stringified
 * rather than dropped — a value we cannot pretty-print is still evidence.
 */
export function formatExtractedValue(value: unknown): string {
    if (value === null || value === undefined) return '—'
    if (typeof value === 'string') return value
    if (typeof value === 'number' || typeof value === 'boolean') return String(value)
    try {
        return JSON.stringify(value)
    } catch {
        return String(value)
    }
}

function normalizeTiming(raw: Record<string, any> | null | undefined): ImportTiming | null {
    if (!raw || typeof raw !== 'object' || !raw.deposited_at) return null
    const n = (v: unknown) => Number(v) || 0
    const figures = raw.figures ?? {}
    const docs = raw.documents ?? {}
    return {
        live: Boolean(raw.live),
        machine_ms: n(raw.machine_ms),
        read_ms: n(raw.read_ms),
        extraction_ms: n(raw.extraction_ms),
        go_wait_ms: n(raw.go_wait_ms),
        elapsed_ms: n(raw.elapsed_ms),
        waiting_for_go_since: raw.waiting_for_go_since ?? null,
        figures: {
            read_extract_ms: n(figures.read_extract_ms),
            generate_ms: n(figures.generate_ms),
            agents_ms: n(figures.agents_ms),
            total_ms: n(figures.total_ms),
        },
        tiers: Array.isArray(raw.tiers)
            ? raw.tiers.map((t: Record<string, any>) => ({ tier: n(t.tier), released_at: String(t.released_at), settled_at: t.settled_at ?? null }))
            : [],
        documents: {
            count: n(docs.count),
            settled: n(docs.settled),
            pages: n(docs.pages),
            p50_machine_ms: n(docs.p50_machine_ms),
            p95_machine_ms: n(docs.p95_machine_ms),
        },
        by_job_type: Array.isArray(raw.by_job_type)
            ? raw.by_job_type.map((b: Record<string, any>) => ({
                job_type: String(b.job_type),
                jobs: n(b.jobs),
                executions: n(b.executions),
                run_ms: n(b.run_ms),
                p95_run_ms: n(b.p95_run_ms),
                queue_ms: n(b.queue_ms),
            }))
            : [],
        after_extraction: {
            documents: n(raw.after_extraction?.documents),
            last_end: raw.after_extraction?.last_end ?? null,
        },
    }
}
